import classNames from "classnames";
import Dropzone, { Accept } from "react-dropzone";
import {
  Control,
  Controller,
  useWatch,
  UseFormRegister,
  UseFormSetValue
} from "react-hook-form";
import React, { useState, memo, ChangeEvent, useEffect } from "react";
import {
  DocumentTextIcon,
  EyeIcon,
  PhotoIcon,
  TrashIcon,
  ArrowDownTrayIcon
} from "@heroicons/react/24/outline";

import Label from "../../atomic/Label";
import TextArea from "../../atomic/TextArea";

import { FileUploadRejectionReason } from "./constants";

type Props = {
  accept: Accept;
  maxFiles: number;
  onDropAccepted: (files: File[], comments?: { [key: string]: string }) => void;
  onDropRejected: (reason?: FileUploadRejectionReason) => void;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  label: string;
  name: string;
  styles?: {
    wrapper?: string;
    inputContainer?: string;
    fileIcon?: {
      container?: string;
      title?: string;
      wrapper?: string;
      marginTop?: string;
    };
  };
  customText: React.ReactNode;
  outsideView: boolean;
  isRequired?: boolean;
  showComment?: boolean;
  commentFieldName?: string;
  required?: boolean;
  disabled?: boolean;
  reset?: () => void;
  apiOnlyView?: boolean;
  apiFileName?: string;
  apiFileId?: number;
  viewDocument?: (file: File) => void;
  register?: UseFormRegister<any>;
  onOpenClick?: (openFn: () => void) => void;
  showTakePhoto?: boolean;
};

const FileUploadInput = memo(function FileUploadInput({
  accept,
  maxFiles = 100,
  onDropAccepted,
  onDropRejected,
  control,
  setValue,
  label,
  name,
  styles = {},
  customText,
  outsideView = false,
  isRequired = false,
  showComment = false,
  commentFieldName,
  required = true,
  disabled = false,
  reset,
  viewDocument,
  register,
  onOpenClick,
  showTakePhoto = false
}: Props) {
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);
  const [fileComments, setFileComments] = useState<{ [key: string]: string }>(
    {}
  );
  const watchedValue = useWatch({
    control,
    name
  });

  useEffect(() => {
    if (commentFieldName) {
      setValue(commentFieldName, fileComments, {
        shouldValidate: true
      });
    }
  }, []);

  useEffect(() => {
    if (
      !watchedValue ||
      (Array.isArray(watchedValue) && watchedValue.length === 0)
    ) {
      // Form was reset — clear internal list
      setAcceptedFiles([]);
    } else if (watchedValue instanceof File) {
      // Single file set externally (e.g. camera capture via setValue)
      setAcceptedFiles([watchedValue]);
    } else if (
      Array.isArray(watchedValue) &&
      watchedValue.length > 0 &&
      watchedValue.every((f: unknown) => f instanceof File)
    ) {
      // File array set externally — sync it in
      setAcceptedFiles(watchedValue as File[]);
    }
  }, [watchedValue]);

  const handleCommentChange = (fileId: number, comment: string) => {
    const updatedComments = { ...fileComments, [fileId]: comment };
    setFileComments(updatedComments);
    if (commentFieldName) {
      setValue(commentFieldName, updatedComments, {
        shouldValidate: true
      });
    }
    onDropAccepted(acceptedFiles, updatedComments);
  };

  const viewFile = (fileId: number) => {
    viewDocument?.(
      acceptedFiles.find((file) => file.lastModified === fileId) as File
    );
  };

  const deleteFile = (fileId: number) => {
    const updatedFiles = acceptedFiles.filter(
      (file) => file.lastModified !== fileId
    );

    const updatedComments = { ...fileComments };
    delete updatedComments[fileId];

    setFileComments(updatedComments);
    setValue(name, updatedFiles as unknown as FileList, {
      shouldValidate: true
    });
    setAcceptedFiles(updatedFiles);
    onDropAccepted(updatedFiles, updatedComments);

    if (commentFieldName) {
      setValue(commentFieldName, updatedComments, {
        shouldValidate: true
      });
    }
  };

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        validate: (val) => {
          return required ? val?.length !== 0 : true;
        }
      }}
      render={({ field: { onChange, onBlur }, fieldState }) => {
        return (
          <Dropzone
            onDrop={(files) => {
              const duplicateFiles = files?.filter((file) =>
                acceptedFiles?.some((f) => f.lastModified === file.lastModified)
              );
              const totalFiles = acceptedFiles.length + files.length;

              if (duplicateFiles?.length > 0) {
                onDropRejected(FileUploadRejectionReason.DUPLICATE);
                return;
              }

              if (totalFiles > maxFiles) {
                onDropRejected(FileUploadRejectionReason.MAX_FILES);
                return;
              }

              const newFiles = outsideView
                ? [...acceptedFiles, ...files]
                : files;

              setAcceptedFiles(newFiles);
              // onDropAccepted(newFiles);
              reset?.();
              setValue(name, newFiles as unknown as FileList, {
                shouldValidate: true
              });
            }}
            onDropAccepted={(files) => {
              if (!outsideView) {
                setAcceptedFiles(files);
              }

              onDropAccepted(files);
            }}
            onDropRejected={() => onDropRejected()}
            maxFiles={maxFiles || 1}
            accept={accept}
            disabled={disabled}
          >
            {({ getRootProps, getInputProps, open }) => (
              <div
                className={`${styles?.wrapper} ${disabled ? "cursor-not-allowed" : ""}`}
              >
                <div>
                  <Label name="file" label={label} requiredIcon={isRequired} />
                  <div
                    {...getRootProps()}
                    id="upload-document-text"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (disabled) return;
                      if (onOpenClick && showTakePhoto) {
                        onOpenClick(open);
                      } else {
                        open();
                      }
                    }}
                    className={classNames(
                      `w-full min-h-[250px] rounded-md border-dashed border border-gray-200 px-4 flex items-center justify-center text-sm bg-gray-100`,
                      styles?.inputContainer
                    )}
                    style={{
                      backgroundColor: disabled ? "rgb(243 244 246)" : ""
                    }}
                  >
                    <input
                      {...getInputProps({
                        id: "spreadsheet",
                        onChange,
                        onBlur
                      })}
                    />

                    {outsideView || acceptedFiles?.length === 0 ? (
                      <p className="">
                        <button
                          disabled={disabled}
                          className={`${disabled ? "cursor-not-allowed" : ""}`}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (disabled) return;
                            if (onOpenClick && showTakePhoto) {
                              onOpenClick(open);
                            } else {
                              open();
                            }
                          }}
                        >
                          {customText ? customText : "Choose a file"}
                        </button>{" "}
                        {!customText && "or drag and drop"}
                      </p>
                    ) : null}
                  </div>

                  {!outsideView && (
                    <div className="flex flex-col items-center justify-center text-sm mb-1">
                      {acceptedFiles.length
                        ? acceptedFiles.map((file) => (
                            <FileIcon
                              key={file?.lastModified}
                              fileName={file?.name}
                              style={styles?.fileIcon}
                              showComment={showComment}
                              onCommentChange={(comment) =>
                                handleCommentChange(file?.lastModified, comment)
                              }
                            />
                          ))
                        : null}
                    </div>
                  )}
                </div>

                {outsideView && (
                  <div className="flex flex-col gap-2">
                    {acceptedFiles?.map((file) => (
                      <FileIcon
                        key={file?.lastModified}
                        viewFile={() => viewFile(file?.lastModified)}
                        deleteFile={() => deleteFile(file?.lastModified)}
                        fileName={file?.name}
                        style={{
                          container: classNames(
                            "p-4 rounded-xl mt-3 border justify-between",
                            styles?.fileIcon?.container ?? "",
                            styles?.fileIcon?.marginTop ?? ""
                          ),
                          title: classNames(
                            "w-60",
                            styles?.fileIcon?.title ?? ""
                          ),
                          wrapper: classNames(
                            "flex flex-col",
                            styles?.fileIcon?.wrapper ?? ""
                          )
                        }}
                        showComment={showComment}
                        onCommentChange={(comment) =>
                          handleCommentChange(file?.lastModified, comment)
                        }
                      />
                    ))}
                  </div>
                )}

                {fieldState.error && (
                  <div className="mt-1">
                    <span className="text-red-600 text-xs" role="alert">
                      {fieldState.error.message}
                    </span>
                  </div>
                )}
              </div>
            )}
          </Dropzone>
        );
      }}
    />
  );
});

export const FileIcon = ({
  fileName,
  style,
  deleteFile,
  showComment,
  onCommentChange,
  viewFile,
  downloadFile
}: {
  fileName: string;
  deleteFile?: () => void;
  style?: {
    icon?: string;
    container?: string;
    title?: string;
    wrapper?: string;
  };
  showComment: boolean;
  onCommentChange?: (comment: string) => void;
  viewFile?: () => void;
  downloadFile?: () => void;
}) => {
  const handleCommentChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const newComment = event?.target?.value;
    onCommentChange?.(newComment);
  };

  const getFileIcon = (fileType: string) => {
    return fileType === "image" ? (
      <PhotoIcon
        className={classNames("h-5 w-5 text-inherit", style?.icon ?? "")}
      />
    ) : (
      <DocumentTextIcon
        className={classNames("h-5 w-5 text-inherit", style?.icon ?? "")}
      />
    );
  };

  const fileExtension = fileName?.split(".").pop()?.toLowerCase() as string;
  const fileType = ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(
    fileExtension
  )
    ? "image"
    : "document";

  return (
    <div className={classNames("flex items-center", style?.wrapper ?? "")}>
      <div
        className={classNames(
          "flex items-center w-full",
          style?.container ?? ""
        )}
      >
        <div
          className={classNames(
            "flex items-center gap-2 max-w-[60%] overflow-hidden"
          )}
        >
          <div>{getFileIcon(fileType)}</div>
          <div
            className={classNames(
              "text-xs ml-1 w-32 overflow-hidden overflow-ellipsis whitespace-nowrap",
              style?.title ?? ""
            )}
          >
            {fileName}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {viewFile && (
            <EyeIcon
              className="h-5 w-5 text-[#667085] cursor-pointer hover:text-[#B42318]"
              onClick={viewFile}
            />
          )}

          {downloadFile && (
            <ArrowDownTrayIcon
              className="h-5 w-5 text-[#667085] cursor-pointer hover:text-[#B42318]"
              onClick={downloadFile}
            />
          )}

          {deleteFile && (
            <TrashIcon
              className="h-5 w-5 text-[#667085] cursor-pointer hover:text-[#B42318]"
              onClick={deleteFile}
            />
          )}
        </div>
      </div>
      {showComment && (
        <TextArea
          placeholder="Add a comment"
          styles={{
            container: "w-full mb-2 rounded-xl"
          }}
          onChange={handleCommentChange}
        />
      )}
    </div>
  );
};

export default FileUploadInput;
